import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext, FormikValues, Field } from 'formik';
import { ToggleField, Select, FieldHelp, Button } from '../../../components';
import type { Value } from '../../../components';
import { RootState, useAppDispatch, useAppSelector, loadProjects } from '../../../redux';
import { Project } from '../../../models';

/** Dev change `type` values (legacy enum). */
const DEV_TYPES = ['fix', 'feature', 'ui', 'i18n', 'refactor', 'test', 'doc', 'lint', 'build', 'release', 'chore'];
/** Non-dev `typeSubtype` / intake defaults (e.g. support). */
const NON_DEV_TYPES = ['support', 'sales', 'oncall', 'abuse', 'ops', 'other'];

const DEFAULT_FROM_PLACEHOLDER = 'Net Oxygen <yoda@netoxygen.ch>';
const OVERRIDE_FROM_PLACEHOLDER = 'Support Net Oxygen <support@netoxygen.ch>';

function EmailIntakeForm() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { values, setFieldValue } = useFormikContext<FormikValues>();

    useEffect(() => {
        dispatch(loadProjects());
    }, [dispatch]);

    const projectOptions = useMemo(() => {
        const list = (projects || []).slice().sort((a: Project, b: Project) => a.name.localeCompare(b.name));
        const currentId = (values.email_intake as Record<string, unknown> | undefined)?.default_project as string | undefined;
        const extra = currentId && !list.some((p: Project) => p._id === currentId)
            ? [{ value: currentId, label: currentId }]
            : [];
        return [
            { value: '', label: t('settings.email_intake_not_set') },
            ...extra,
            ...list.map((p: Project) => ({ value: p._id, label: p.name })),
        ];
    }, [projects, t, values.email_intake]);

    const typeOptions = useMemo(() => {
        const all = [...NON_DEV_TYPES, ...DEV_TYPES];
        const currentType = (values.email_intake as Record<string, unknown> | undefined)?.default_type as string | undefined;
        if (currentType && !all.includes(currentType))
            all.unshift(currentType);
        return [
            { value: '', label: t('settings.email_intake_not_set') },
            ...all.map((typeKey) => ({ value: typeKey, label: typeKey })),
        ];
    }, [t, values.email_intake]);

    const emailIntake = (values.email_intake || {}) as Record<string, unknown>;
    const defaultProject = (emailIntake.default_project as string | undefined) ?? '';
    const defaultType = (emailIntake.default_type as string | undefined) ?? '';
    const fromByType = (emailIntake.from_by_type && typeof emailIntake.from_by_type === 'object')
        ? { ...(emailIntake.from_by_type as Record<string, string>) }
        : {};

    const overrideEntries = useMemo(
        () => Object.entries(fromByType)
            .filter(([key]) => key !== 'default')
            .sort(([a], [b]) => a.localeCompare(b)),
        [fromByType]
    );

    const subtypeLabel = (key: string) => {
        const translated = t(`settings.email_intake_from_by_type_${key}`, { defaultValue: '' });
        if (translated && translated !== `settings.email_intake_from_by_type_${key}`)
            return translated;
        return key;
    };

    const availableOverrideOptions = useMemo(() => {
        const used = new Set(overrideEntries.map(([key]) => key));
        return NON_DEV_TYPES
            .filter((key) => !used.has(key))
            .map((key) => ({ value: key, label: subtypeLabel(key) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subtypeLabel depends on t
    }, [overrideEntries, t]);

    const setFromByType = (next: Record<string, string>) => {
        setFieldValue('email_intake.from_by_type', next);
    };

    const updateOverrideFrom = (key: string, from: string) => {
        setFromByType({ ...fromByType, [key]: from });
    };

    const removeOverride = (key: string) => {
        const next = { ...fromByType };
        delete next[key];
        setFromByType(next);
    };

    const addOverride = (typeKey: Value) => {
        const key = String(typeKey || '').trim();
        if (!key || key === 'default' || fromByType[key] !== undefined)
            return;
        setFromByType({ ...fromByType, [key]: '' });
    };

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                    {t('settings.email_intake')}
                    <FieldHelp content={t('settings.help.email_intake') || ''} />
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('settings.email_intake_desc')}
                </p>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="enable" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_email_intake')}
                        </label>
                        <ToggleField name="email_intake.enabled" className="inline" />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="default_project" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.email_intake_default_project')}
                    </label>
                    <div className="mt-2">
                        <Select
                            className="!mt-0"
                            defaultValue={defaultProject}
                            onChange={(v: Value) => setFieldValue('email_intake.default_project', v)}
                            options={projectOptions}
                            containerClassName="relative w-full cursor-default rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 sm:text-sm"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="default_type" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.email_intake_default_type')}
                    </label>
                    <div className="mt-2">
                        <Select
                            className="!mt-0"
                            defaultValue={defaultType}
                            onChange={(v: Value) => setFieldValue('email_intake.default_type', v)}
                            options={typeOptions}
                            containerClassName="relative w-full cursor-default rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 sm:text-sm"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="default_estimate" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.email_intake_default_estimate')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="number"
                            min="1"
                            step="1"
                            name="email_intake.default_estimate"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="default_deadline" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.email_intake_default_deadline')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="number"
                            min="0"
                            step="1"
                            name="email_intake.default_deadline"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                    <small>{t('settings.email_intake_default_deadline_desc')}</small>
                </div>
                <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.email_intake_from_by_type')}
                        <FieldHelp content={t('settings.help.email_intake_from_by_type') || ''} />
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.email_intake_from_by_type_desc')}
                    </p>
                    <div className="mt-5">
                        <label
                            htmlFor="from_by_type_default"
                            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                        >
                            {t('settings.email_intake_from_by_type_default')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="text"
                                id="from_by_type_default"
                                name="email_intake.from_by_type.default"
                                placeholder={DEFAULT_FROM_PLACEHOLDER}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>
                    {overrideEntries.length > 0 && (
                        <div className="mt-5 space-y-4">
                            <div className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.email_intake_from_by_type_overrides')}
                            </div>
                            {overrideEntries.map(([key, fromValue]) => (
                                <div key={key} className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                    <div className="sm:w-48">
                                        <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                            {t('settings.email_intake_from_by_type_override_type')}
                                        </label>
                                        <div className="mt-2 rounded-md bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                                            {subtypeLabel(key)}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <label
                                            htmlFor={`from_by_type_${key}`}
                                            className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                        >
                                            {t('settings.email_intake_from_by_type_override_from')}
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                id={`from_by_type_${key}`}
                                                value={fromValue ?? ''}
                                                onChange={(e) => updateOverrideFrom(key, e.target.value)}
                                                placeholder={OVERRIDE_FROM_PLACEHOLDER}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>
                                    <div className="pb-0.5">
                                        <Button
                                            type="button"
                                            onClick={() => removeOverride(key)}
                                            title={t('common.delete') || ''}
                                            color="danger"
                                            icon="delete"
                                            small
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {availableOverrideOptions.length > 0 && (
                        <div className="mt-5">
                            <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.email_intake_from_by_type_add')}
                            </label>
                            <div className="mt-2 max-w-xs">
                                <Select
                                    key={`add-from-override-${availableOverrideOptions.map((o) => o.value).join('-')}`}
                                    className="!mt-0"
                                    defaultValue=""
                                    onChange={addOverride}
                                    options={[
                                        { value: '', label: t('settings.email_intake_from_by_type_add_placeholder') },
                                        ...availableOverrideOptions,
                                    ]}
                                    containerClassName="relative w-full cursor-default rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 sm:text-sm"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.email_intake_ack')}
                        <FieldHelp content={t('settings.help.email_intake_ack') || ''} />
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.email_intake_ack_desc')}
                    </p>
                    <div className="mt-4 flex space-between">
                        <label htmlFor="ack_enabled" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_ack_enabled')}
                        </label>
                        <ToggleField name="email_intake.ack.enabled" className="inline" />
                    </div>
                    <div className="mt-5">
                        <label htmlFor="ack_subject" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_ack_subject')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="text"
                                name="email_intake.ack.subject"
                                placeholder={t('settings.email_intake_ack_subject_placeholder') as string}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                        </div>
                        <small>{t('settings.email_intake_ack_subject_desc')}</small>
                    </div>
                    <div className="mt-5">
                        <label htmlFor="ack_body" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_ack_body')}
                        </label>
                        <div className="mt-2">
                            <Field
                                as="textarea"
                                rows={8}
                                name="email_intake.ack.body"
                                placeholder={t('settings.email_intake_ack_body_placeholder') as string}
                                className="block w-full rounded-md border-0 py-1.5 font-mono text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                            />
                        </div>
                        <small>{t('settings.email_intake_ack_body_desc')}</small>
                    </div>
                    <div className="mt-5">
                        <label htmlFor="ack_suppress_to" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_ack_suppress_to')}
                        </label>
                        <div className="mt-2">
                            <Field
                                as="textarea"
                                rows={4}
                                name="email_intake.ack.suppress_to"
                                placeholder={t('settings.email_intake_ack_suppress_to_placeholder') as string}
                                className="block w-full rounded-md border-0 py-1.5 font-mono text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                            />
                        </div>
                        <small>{t('settings.email_intake_ack_suppress_to_desc')}</small>
                    </div>
                </div>

                <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.email_intake_spam_allowlist')}
                        <FieldHelp content={t('settings.help.email_intake_spam_allowlist') || ''} />
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.email_intake_spam_allowlist_desc')}
                    </p>
                    <div className="mt-5">
                        <label htmlFor="spam_allowlist_emails" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_spam_allowlist_emails')}
                        </label>
                        <div className="mt-2">
                            <Field
                                as="textarea"
                                rows={4}
                                name="email_intake.spam_allowlist.emails"
                                placeholder={t('settings.email_intake_spam_allowlist_emails_placeholder') as string}
                                className="block w-full rounded-md border-0 py-1.5 font-mono text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                            />
                        </div>
                        <small>{t('settings.email_intake_spam_allowlist_emails_desc')}</small>
                    </div>
                    <div className="mt-5">
                        <label htmlFor="spam_allowlist_domains" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.email_intake_spam_allowlist_domains')}
                        </label>
                        <div className="mt-2">
                            <Field
                                as="textarea"
                                rows={4}
                                name="email_intake.spam_allowlist.domains"
                                placeholder={t('settings.email_intake_spam_allowlist_domains_placeholder') as string}
                                className="block w-full rounded-md border-0 py-1.5 font-mono text-sm text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                            />
                        </div>
                        <small>{t('settings.email_intake_spam_allowlist_domains_desc')}</small>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EmailIntakeForm;
